import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    CalendarDaysIcon,
    UsersIcon,
    UserGroupIcon,
    DevicePhoneMobileIcon,
    EnvelopeIcon,
    IdentificationIcon,
    ChartBarIcon,
    TruckIcon,
    CheckCircleIcon,
    ClockIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getUserAnalytics, getDriverAnalytics } from "../api/analyticsApi";
import type { AnalyticsDateRange } from "../types";

// ============================================================================
// Helpers
// ============================================================================

const fmtNumber = (n: number | null | undefined) => {
    if (n == null) return "—";
    return new Intl.NumberFormat("en-NG").format(n);
};

const fmtCurrency = (amount: number | null | undefined) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
    }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const UserAnalyticsRoute = () => {
    const [dateRange, setDateRange] = useState<AnalyticsDateRange>({});

    const {
        data: userData,
        isLoading: userLoading,
        error: userError,
        refetch,
        isFetching,
    } = useQuery({
        queryKey: ["analytics-users", dateRange],
        queryFn: () => getUserAnalytics(dateRange),
    });

    const {
        data: driverData,
        isLoading: driverLoading,
        error: driverError,
    } = useQuery({
        queryKey: ["analytics-drivers", dateRange],
        queryFn: () => getDriverAnalytics(dateRange),
    });

    const users = userData?.data;
    const drivers = driverData?.data;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (userLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (userError) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">
                    Failed to load user analytics
                </h3>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Date Range */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                    <input
                        type="date"
                        value={dateRange.startDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, startDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <span className="text-sm text-slate-400">to</span>
                    <input
                        type="date"
                        value={dateRange.endDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, endDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {/* ================================================================
                User Analytics
               ================================================================ */}

            {/* User Summary Cards */}
            {users?.summary && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex items-center gap-3">
                            <div className="rounded-xl bg-violet-50 p-2 dark:bg-violet-900/20">
                                <UsersIcon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Total Users</p>
                                <p className="text-xl font-bold text-slate-900 dark:text-white">
                                    {fmtNumber(users.summary.totalUsers)}
                                </p>
                            </div>
                        </div>
                        <p className="mt-1 text-xs text-slate-400">
                            {fmtNumber(users.summary.totalRiders)} riders · {fmtNumber(users.summary.totalDrivers)} drivers
                        </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <p className="text-sm text-slate-500 dark:text-slate-400">New This Period</p>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                            {fmtNumber(users.summary.newUsersThisPeriod)}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                            {fmtNumber(users.summary.activeUsersThisPeriod)} active
                        </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Retention Rate</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                            {users.summary.retentionRate?.toFixed(1)}%
                        </p>
                    </div>
                </div>
            )}

            {/* User Engagement */}
            {users?.userEngagement && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        User Engagement
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-4">
                        <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                            <p className="text-xs text-slate-500 dark:text-slate-400">DAU</p>
                            <p className="text-lg font-bold text-slate-900 dark:text-white">
                                {fmtNumber(users.userEngagement.dailyActiveUsers)}
                            </p>
                        </div>
                        <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                            <p className="text-xs text-slate-500 dark:text-slate-400">WAU</p>
                            <p className="text-lg font-bold text-slate-900 dark:text-white">
                                {fmtNumber(users.userEngagement.weeklyActiveUsers)}
                            </p>
                        </div>
                        <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                            <p className="text-xs text-slate-500 dark:text-slate-400">MAU</p>
                            <p className="text-lg font-bold text-slate-900 dark:text-white">
                                {fmtNumber(users.userEngagement.monthlyActiveUsers)}
                            </p>
                        </div>
                        <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                            <p className="text-xs text-slate-500 dark:text-slate-400">Avg Trips/User</p>
                            <p className="text-lg font-bold text-slate-900 dark:text-white">
                                {users.userEngagement.averageTripsPerActiveUser?.toFixed(1)}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Verification Stats */}
            {users?.verificationStats && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ShieldCheckIcon className="h-4 w-4" /> Verification Stats
                    </h3>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <DevicePhoneMobileIcon className="h-4 w-4 text-slate-400" />
                            <span className="w-32 text-sm text-slate-700 dark:text-slate-300">Phone</span>
                            <div className="flex-1">
                                <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                    <div
                                        className="h-full rounded-full bg-emerald-500"
                                        style={{ width: `${users.verificationStats.phoneVerificationRate}%` }}
                                    />
                                </div>
                            </div>
                            <span className="w-24 text-right text-sm text-slate-500 dark:text-slate-400">
                                {fmtNumber(users.verificationStats.phoneVerified)} ({users.verificationStats.phoneVerificationRate?.toFixed(1)}%)
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <EnvelopeIcon className="h-4 w-4 text-slate-400" />
                            <span className="w-32 text-sm text-slate-700 dark:text-slate-300">Email</span>
                            <div className="flex-1">
                                <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                    <div
                                        className="h-full rounded-full bg-blue-500"
                                        style={{ width: `${users.verificationStats.emailVerificationRate}%` }}
                                    />
                                </div>
                            </div>
                            <span className="w-24 text-right text-sm text-slate-500 dark:text-slate-400">
                                {fmtNumber(users.verificationStats.emailVerified)} ({users.verificationStats.emailVerificationRate?.toFixed(1)}%)
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <IdentificationIcon className="h-4 w-4 text-slate-400" />
                            <span className="w-32 text-sm text-slate-700 dark:text-slate-300">Identity</span>
                            <div className="flex-1">
                                <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                    <div
                                        className="h-full rounded-full bg-violet-500"
                                        style={{ width: `${((users.verificationStats.identityVerified / (users.summary?.totalUsers || 1)) * 100)}%` }}
                                    />
                                </div>
                            </div>
                            <span className="w-24 text-right text-sm text-slate-500 dark:text-slate-400">
                                {fmtNumber(users.verificationStats.identityVerified)}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* User Growth Chart */}
            {users?.userGrowth && users.userGrowth.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ChartBarIcon className="h-4 w-4" /> User Growth
                    </h3>

                    <div className="mb-4 flex items-end gap-1" style={{ height: 100 }}>
                        {users.userGrowth.map((day) => {
                            const max = Math.max(...users.userGrowth.map((d) => d.newUsers), 1);
                            const pct = (day.newUsers / max) * 100;
                            return (
                                <div
                                    key={day.date}
                                    className="group relative flex-1"
                                    title={`${day.date}: ${day.newUsers} new (${day.newRiders} riders, ${day.newDrivers} drivers)`}
                                >
                                    <div
                                        className="w-full rounded-t bg-gradient-to-t from-violet-600 to-violet-400 transition-all group-hover:from-violet-700 group-hover:to-violet-500"
                                        style={{ height: `${pct}%`, minHeight: day.newUsers ? 4 : 0 }}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    <div className="max-h-48 overflow-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Date</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">New Users</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Riders</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Drivers</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {users.userGrowth.map((day) => (
                                    <tr key={day.date}>
                                        <td className="px-3 py-2 text-slate-900 dark:text-white">{day.date}</td>
                                        <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{fmtNumber(day.newUsers)}</td>
                                        <td className="px-3 py-2 text-violet-600 dark:text-violet-400">{fmtNumber(day.newRiders)}</td>
                                        <td className="px-3 py-2 text-amber-600 dark:text-amber-400">{fmtNumber(day.newDrivers)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ================================================================
                Driver Analytics
               ================================================================ */}
            <div className="border-t border-slate-200 pt-6 dark:border-slate-700">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
                    <UserGroupIcon className="h-5 w-5 text-amber-500" /> Driver Analytics
                </h2>

                {driverLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                    </div>
                ) : driverError ? (
                    <p className="text-sm text-rose-500">Failed to load driver analytics</p>
                ) : drivers ? (
                    <div className="space-y-5">
                        {/* Driver Summary */}
                        {drivers.summary && (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Total</p>
                                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                                        {fmtNumber(drivers.summary.totalDrivers)}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Active</p>
                                    <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                                        {fmtNumber(drivers.summary.activeDrivers)}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">New</p>
                                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                        {fmtNumber(drivers.summary.newDriversThisPeriod)}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Pending</p>
                                    <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                        {fmtNumber(drivers.summary.pendingVerification)}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Suspended</p>
                                    <p className="text-xl font-bold text-rose-600 dark:text-rose-400">
                                        {fmtNumber(drivers.summary.suspendedDrivers)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Onboarding Funnel */}
                        {drivers.onboardingFunnel && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                    <CheckCircleIcon className="h-4 w-4" /> Onboarding Funnel
                                </h3>
                                <div className="space-y-2">
                                    {[
                                        { label: "Registered", value: drivers.onboardingFunnel.registered },
                                        { label: "Docs Submitted", value: drivers.onboardingFunnel.documentsSubmitted },
                                        { label: "Docs Verified", value: drivers.onboardingFunnel.documentsVerified },
                                        { label: "First Trip", value: drivers.onboardingFunnel.firstTripCompleted },
                                    ].map((step, i) => {
                                        const max = drivers.onboardingFunnel.registered || 1;
                                        const pct = (step.value / max) * 100;
                                        return (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="w-32 text-sm text-slate-700 dark:text-slate-300">
                                                    {step.label}
                                                </span>
                                                <div className="flex-1">
                                                    <div className="h-4 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                                        <div
                                                            className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400"
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <span className="w-16 text-right text-sm font-medium text-slate-900 dark:text-white">
                                                    {fmtNumber(step.value)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="mt-2 text-xs text-slate-400">
                                    Conversion: {drivers.onboardingFunnel.conversionRate?.toFixed(1)}%
                                </p>
                            </div>
                        )}

                        {/* Driver Activity */}
                        {drivers.driverActivity && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                    <ClockIcon className="h-4 w-4" /> Driver Activity (Daily Avg)
                                </h3>
                                <div className="grid gap-3 sm:grid-cols-4">
                                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Online Hours</p>
                                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                                            {drivers.driverActivity.averageOnlineHoursPerDay?.toFixed(1)}h
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Trips</p>
                                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                                            {drivers.driverActivity.averageTripsPerDay?.toFixed(1)}
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Earnings</p>
                                        <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                                            {fmtCurrency(drivers.driverActivity.averageEarningsPerDay)}
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Accept Rate</p>
                                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                                            {drivers.driverActivity.averageAcceptanceRate?.toFixed(1)}%
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Performance Distribution + Vehicle Breakdown */}
                        <div className="grid gap-6 lg:grid-cols-2">
                            {drivers.performanceDistribution && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                    <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        Performance Distribution
                                    </h3>
                                    <div className="space-y-2">
                                        {[
                                            { label: "Top Performers", value: drivers.performanceDistribution.topPerformers, color: "bg-emerald-500" },
                                            { label: "Good", value: drivers.performanceDistribution.goodPerformers, color: "bg-blue-500" },
                                            { label: "Average", value: drivers.performanceDistribution.averagePerformers, color: "bg-amber-500" },
                                            { label: "Needs Improvement", value: drivers.performanceDistribution.needsImprovement, color: "bg-rose-500" },
                                        ].map((cat) => {
                                            const total =
                                                drivers.performanceDistribution.topPerformers +
                                                drivers.performanceDistribution.goodPerformers +
                                                drivers.performanceDistribution.averagePerformers +
                                                drivers.performanceDistribution.needsImprovement || 1;
                                            const pct = (cat.value / total) * 100;
                                            return (
                                                <div key={cat.label} className="flex items-center gap-3">
                                                    <span className="w-36 text-sm text-slate-700 dark:text-slate-300">
                                                        {cat.label}
                                                    </span>
                                                    <div className="flex-1">
                                                        <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                                            <div className={`h-full rounded-full ${cat.color}`} style={{ width: `${pct}%` }} />
                                                        </div>
                                                    </div>
                                                    <span className="w-12 text-right text-sm text-slate-500 dark:text-slate-400">
                                                        {fmtNumber(cat.value)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {drivers.vehicleBreakdown && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        <TruckIcon className="h-4 w-4" /> Vehicle Breakdown
                                    </h3>
                                    <div className="space-y-2">
                                        {Object.entries(drivers.vehicleBreakdown).map(([type, count]) => {
                                            const max = Math.max(...Object.values(drivers.vehicleBreakdown), 1);
                                            const pct = (count / max) * 100;
                                            return (
                                                <div key={type} className="flex items-center gap-3">
                                                    <span className="w-24 text-sm text-slate-700 dark:text-slate-300">
                                                        {type}
                                                    </span>
                                                    <div className="flex-1">
                                                        <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                                            <div className="h-full rounded-full bg-slate-400 dark:bg-slate-500" style={{ width: `${pct}%` }} />
                                                        </div>
                                                    </div>
                                                    <span className="w-12 text-right text-sm text-slate-500 dark:text-slate-400">
                                                        {fmtNumber(count)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Date range */}
            {users?.dateRange && (
                <p className="text-right text-xs text-slate-400 dark:text-slate-500">
                    Period: {users.dateRange.startDate} — {users.dateRange.endDate}
                </p>
            )}
        </div>
    );
};
